/**
 * 品目の段階と進捗の表示。一覧と品目詳細で同じ見た目にするための部品。
 *
 * 判定は lib/progress.ts（純ロジック）が行い、ここは出すだけ。
 * 二重に判定を書くと、一覧と詳細で違う段階を出す（実際に status バッジと
 * 本文の案内が食い違っていた）。
 */
import { useI18n } from "../lib/i18n";
import {
  FAILED_KIND_BADGE,
  FAILED_KIND_LABEL,
  FAILED_LABEL,
  progressOf,
  RUNNING_LABEL,
  STAGE_FLOW,
  STAGE_HELP,
  STAGE_LABEL,
  type Progress,
} from "../lib/progress";
import { materialsOf } from "../lib/materials";
import { type DishDoc } from "../lib/types";

/** 段階のバッジ。色は styles.css の .stage-* が持つ */
export function StageBadge({ p }: { p: Progress }) {
  const { t } = useI18n();
  const label =
    p.stage === "generating" && p.jobType
      ? t(RUNNING_LABEL[p.jobType])
      : p.stage === "failed" && p.failedKind
        ? t(FAILED_KIND_BADGE[p.failedKind])
        : t(STAGE_LABEL[p.stage]);
  return (
    <span className={`badge stage-${p.stage}`} title={t(STAGE_HELP[p.stage])}>
      {label}
    </span>
  );
}

/**
 * レビューの進捗バー。
 *
 * 進捗率が出せない品目（この機能より前に生成したもの・確認事項ゼロ）では
 * 何も出さない。0% と出すと「誰も手を付けていない」と誤読させる。
 */
export function ReviewBar({ p }: { p: Progress }) {
  if (p.percent === null) return null;
  return (
    <span className="stage-progress">
      <span className="stage-bar">
        <span className="stage-fill" style={{ width: `${p.percent}%` }} />
      </span>
      <span className="stage-num">{p.percent}%</span>
    </span>
  );
}

/** バッジの下に出す一行。「次に何をすればよいか」が分かる粒度にする */
export function StageDetail({ dish, p }: { dish: DishDoc; p: Progress }) {
  const { t, locale } = useI18n();

  if (p.stage === "generating") {
    // 起動待ちのまま止まることがある（Cloud Run Jobs の起動失敗）。
    // 実行中との区別が付かないと、待てばよいのか押し直すのかが分からない
    if (dish.activeJob?.status === "queued") {
      return <span className="muted small">{t("起動待ち")}</span>;
    }
    const started = dish.activeJob?.startedAt;
    // 十数分かかる処理なので、いつ始まったかが「まだ待つべきか」の判断材料になる
    return started ? (
      <span className="muted small">
        {t("{time} から実行中", { time: started.toDate().toLocaleTimeString(locale) })}
      </span>
    ) : null;
  }
  if (p.stage === "registering") {
    return (
      <span className="muted small">
        {materialsOf(dish).videos.length === 0 ? t("動画の登録待ち") : t("生成の実行待ち")}
      </span>
    );
  }
  if (p.stage === "failed" && p.failedKind) {
    // 再実行では直らない失敗。「再実行が要ります」と出すと同じ操作を繰り返させる
    return (
      <span className="small error">
        {t(FAILED_KIND_LABEL[p.failedKind], { name: dish.failedJob?.ingredient ?? "" })}
      </span>
    );
  }
  if (p.stage === "failed") {
    return (
      <span className="small error">
        {t(FAILED_LABEL[p.jobType ?? "analyze"])}
        {t("（再実行が要ります）")}
      </span>
    );
  }
  if (p.stage === "approving") {
    return <span className="muted small">{t("残っている確認事項はありません")}</span>;
  }
  if (p.percent === null) return null;
  return (
    <span className="muted small">
      {t("残り {n} 問", { n: p.remaining })}
      {p.done > 0 && t("（{done} / {total} 問 完了）", { done: p.done, total: p.total })}
      {p.held > 0 && t("・保留 {n}（承認するには回答が要ります）", { n: p.held })}
    </span>
  );
}

/** 一覧の見出し。段階の並び順そのものを凡例にする */
export function StageLegend() {
  const { t } = useI18n();
  return (
    <p className="stage-legend muted small">
      {STAGE_FLOW.map((s, i) => (
        <span key={s}>
          {i > 0 && <span className="arrow">→</span>}
          <span title={t(STAGE_HELP[s])}>{t(STAGE_LABEL[s])}</span>
        </span>
      ))}
    </p>
  );
}

/** 一覧の1セル分。バッジ・バー・内訳をまとめて出す */
export function ProgressCell({ dish }: { dish: DishDoc }) {
  const p = progressOf(dish);
  return (
    <div className="stage-cell">
      <div className="stage-head">
        <StageBadge p={p} />
        <ReviewBar p={p} />
      </div>
      <StageDetail dish={dish} p={p} />
    </div>
  );
}
